import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { AuthProvider } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";

import { routeTree } from "./routeTree.gen";
import { getAuthConfig } from "./lib/auth/config";
import { authBridge } from "./lib/auth/bridge";
import { DEV_AUTH, useSession } from "./lib/auth/session";
import { Toaster } from "./components/ui/toaster";
import { installDevMocks } from "./lib/dev-mocks";

import "./index.css";

installDevMocks();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: authBridge,
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function AppRouter() {
  const session = useSession();

  if (session.isLoading) {
    return <Splash label="Anmeldung wird geprüft …" />;
  }
  if (session.error) {
    return <Splash label={`Anmeldefehler: ${session.error.message}`} tone="error" />;
  }
  return <RouterProvider router={router} />;
}

function Splash({ label, tone }: { label: string; tone?: "error" }) {
  return (
    <div className="grid min-h-screen place-items-center bg-muted/30 p-6">
      <div
        className={
          "w-full max-w-sm rounded-lg border bg-card p-8 text-center text-sm shadow-sm " +
          (tone === "error" ? "text-destructive" : "text-muted-foreground")
        }
      >
        {label}
      </div>
    </div>
  );
}

// Only mount AuthProvider in real-OIDC mode — DevBypass skips Keycloak entirely.
function AuthRoot({ children }: { children: React.ReactNode }) {
  if (DEV_AUTH) return <>{children}</>;
  return (
    <AuthProvider
      {...getAuthConfig()}
      userStore={new WebStorageStateStore({ store: window.localStorage })}
      onSigninCallback={() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
    >
      {children}
    </AuthProvider>
  );
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root container #root not found in index.html");

createRoot(rootEl).render(
  <StrictMode>
    <AuthRoot>
      <QueryClientProvider client={queryClient}>
        <AppRouter />
        <Toaster />
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      </QueryClientProvider>
    </AuthRoot>
  </StrictMode>,
);
